import { useMemo } from "react";
import {
  MessageList as StreamMessageList,
  useChannelStateContext,
  useChatContext,
} from "stream-chat-react";
import { Box, Avatar, Typography, Paper, Divider } from "@mui/material";
import dayjs from "dayjs";

import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const CustomMessageList = () => {
  const { messages, read, channel } = useChannelStateContext();
  const { client } = useChatContext();

  const channelId = channel?.id;
  const currentUserId = client.userID;

  // ✅ compute recipient last read (changes per channel)
  const recipientLastRead = useMemo(() => {
    const others = Object.values(read || {}).filter(
      (r) => r.user?.id !== currentUserId
    );
    if (others.length === 0) return null;
    return others.reduce((latest, r) => {
      const time = new Date(r.last_read).getTime();
      return !latest || time > latest ? time : latest;
    }, null);
  }, [read, currentUserId, channelId]);

  // ✅ compute which messages are read
  const readMessageIds = useMemo(() => {
    if (!recipientLastRead) return [];
    return messages
      .filter(
        (m) =>
          m.user?.id === currentUserId &&
          new Date(m.created_at).getTime() <= recipientLastRead
      )
      .map((m) => m.id);
  }, [messages, recipientLastRead, currentUserId, channelId]);

  const getStatusIcon = (message, isRead) => {
    if (message.status === "failed") {
      return <ErrorOutlineIcon sx={{ fontSize: 14, color: "#f44336" }} />;
    }
    if (isRead) {
      return <DoneAllIcon sx={{ fontSize: 14, color: "#4caf50" }} />;
    }
    if (message.status === "sent" || message.status === "received") {
      return <DoneAllIcon sx={{ fontSize: 14, color: "#9e9e9e" }} />;
    }
    if (message.status === "sending") {
      return <DoneIcon sx={{ fontSize: 14, color: "gray" }} />;
    }
    return null;
  };

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        p: 1,
      }}
    >
      {messages.map((message, index) => {
        const isOwn = message.user?.id === currentUserId;
        const isRead =
          isOwn &&
          recipientLastRead &&
          new Date(message.created_at) <= recipientLastRead;

        // 📅 Check if this message starts a new day
        const prevMessage = messages[index - 1];
        const showDateDivider =
          !prevMessage ||
          !dayjs(prevMessage.created_at).isSame(message.created_at, "day");

        return (
          <Box key={message.id}>
            {showDateDivider && (
              <Divider sx={{ my: 2, color: "rgba(255,255,255,0.5)" }}>
                <Typography
                  variant="caption"
                  sx={{
                    px: 1.5,
                    color: "rgba(255,255,255,0.7)",
                    backgroundColor: "rgba(30,30,30,0.9)",
                    borderRadius: "8px",
                  }}
                >
                  {dayjs(message.created_at).format("MMMM D, YYYY")}
                </Typography>
              </Divider>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: isOwn ? "flex-end" : "flex-start",
                mb: 1.2,
              }}
            >
              {!isOwn && (
                <Avatar
                  src={message.user?.image}
                  alt={message.user?.name}
                  sx={{
                    width: 32,
                    height: 32,
                    mr: 1,
                    alignSelf: "flex-end",
                  }}
                />
              )}

              <Paper
                elevation={0}
                sx={{
                  px: 1.5,
                  py: 1,
                  maxWidth: "70%",
                  background: isOwn
                    ? "linear-gradient(135deg, #283043, #1c2533)"
                    : "#1E1E1E",
                  color: "white",
                  borderRadius: isOwn
                    ? "16px 16px 4px 16px"
                    : "16px 16px 16px 4px",
                }}
              >
                {!isOwn && message.user?.id === "ai_assistant" && (
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#4caf50",
                      color: "white",
                      px: 1,
                      py: 0.3,
                      borderRadius: "8px",
                      fontSize: "0.7rem",
                      fontWeight: "bold",
                      mb: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    AI
                  </Box>
                )}
                {!isOwn && message.user?.id !== "ai_assistant" && (
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#2196f3",
                      color: "white",
                      px: 1,
                      py: 0.3,
                      borderRadius: "8px",
                      fontSize: "0.7rem",
                      fontWeight: "bold",
                      mb: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    USER
                  </Box>
                )}
                <Typography
                  variant="body1"
                  sx={{
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    p: 0,
                    m: 0,
                  }}
                >
                  {message.text.trim()}
                </Typography>

                {/* Delivery status (for own messages only) */}
                {isOwn && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-end"
                    mt={0.5}
                    gap={0.3}
                  >
                    {getStatusIcon(message, isRead)}
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.6)" }}
                    >
                      {dayjs(message.created_at).format("h:mm A")}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {isOwn && (
                <Avatar
                  src={message.user?.image}
                  alt={message.user?.name}
                  sx={{
                    width: 32,
                    height: 32,
                    ml: 1,
                    alignSelf: "flex-end",
                  }}
                />
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default (props) => (
  <StreamMessageList
    {...props}
    Message={CustomMessageList}
    disableDateSeparator
    hideDeletedMessages
  />
);
